import React, { useState, useEffect } from 'react';
import Button from '../../components/elements/button';
import { Search, Map, ExportIcon,  } from '../../assets/icons';
import '../../assets/css/admin/farms.css';
import { farmsApi } from '../../network/apis/farms';
import LoadingSpinner from '../../components/elements/LoadingSpinner';

const AdminFarms = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        status: 'all',
        size: 'all',
        location: 'all'
    });

    const [farms, setFarms] = useState([]);

    const fetchFarms = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await farmsApi.getFarms();
            if (response?.data) {
                // Map backend data to component format
                const mappedFarms = response.data.map(farm => ({
                    id: farm.id,
                    name: farm.name,
                    owner: farm.owner_name || `${farm.owner?.first_name || ''} ${farm.owner?.last_name || ''}`.trim() || 'Unknown',
                    location: farm.city || farm.state || 'N/A',
                    size: getFarmSize(farm.size),
                    capacity: farm.capacity,
                    species: farm.species,
                    waterSource: farm.water_source,
                    registrationDate: farm.created_at ? new Date(farm.created_at).toLocaleDateString() : 'N/A',
                    status: 'active',
                    lastActivity: farm.updated_at ? getTimeAgo(new Date(farm.updated_at)) : 'N/A',
                    totalCases: farm.cases_count || 0,
                    activeCases: farm.active_cases_count || 0
                }));
                setFarms(mappedFarms);
            } else {
                setFarms([]);
            }
        } catch (err) {
            console.error('Failed to fetch farms:', err);
            setError('Failed to load farms');
            setFarms([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFarms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Helper function to categorize farm size
    const getFarmSize = (sizeInAcres) => {
        const size = parseFloat(sizeInAcres) || 0;
        if (size < 5) return 'Small';
        if (size < 20) return 'Medium';
        return 'Large';
    };

    // Helper function to format time ago
    const getTimeAgo = (date) => {
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 60) return `${diffMins} minutes ago`;
        if (diffHours < 24) return `${diffHours} hours ago`;
        if (diffDays < 7) return `${diffDays} days ago`;
        return date.toLocaleDateString();
    };

    // Filter farms based on search and filters
    const filteredFarms = farms.filter(farm => {
        const searchMatch = farm.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           farm.owner.toLowerCase().includes(searchTerm.toLowerCase());
        const statusMatch = filters.status === 'all' || farm.status === filters.status;
        const sizeMatch = filters.size === 'all' || farm.size.toLowerCase() === filters.size;
        const locationMatch = filters.location === 'all' || 
                             farm.location.toLowerCase().includes(filters.location);
        
        return searchMatch && statusMatch && sizeMatch && locationMatch;
    });

    const exportData = () => {
        // Handle export functionality
    };

    if (loading) {
        return (
            <div className="admin-farms">
                <div className="loading-state">
                    <LoadingSpinner />
                    <p>Loading farms...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-farms">
                <div className="error-state">
                    <p>{error}</p>
                    <Button text="Retry" onClick={fetchFarms} />
                </div>
            </div>
        );
    }

    return (
        <div className="admin-farms">
            <div className="page-header">
                <div className="header-content">
                    <h1>Fish Farms</h1>
                    <p>{filteredFarms.length} registered farms</p>
                </div>
                <Button
                    icon={<img src={ExportIcon} alt="" />}
                    text="Export Data"
                    variant="outline"
                    onClick={exportData}
                />
            </div>

            <div className="filters-section">
                <div className="search-bar">
                    <img src={Search} alt="" />
                    <input
                        type="text"
                        placeholder="Search farms..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="filters">
                    <select
                        value={filters.status}
                        onChange={(e) => setFilters({...filters, status: e.target.value})}
                    >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                        <option value="pending">Pending</option>
                    </select>

                    <select
                        value={filters.size}
                        onChange={(e) => setFilters({...filters, size: e.target.value})}
                    >
                        <option value="all">All Sizes</option>
                        <option value="small">Small</option>
                        <option value="medium">Medium</option>
                        <option value="large">Large</option>
                    </select>

                    <select
                        value={filters.location}
                        onChange={(e) => setFilters({...filters, location: e.target.value})}
                    >
                        <option value="all">All Locations</option>
                        <option value="lagos">Lagos</option>
                        <option value="ogun">Ogun</option>
                        <option value="oyo">Oyo</option>
                    </select>
                </div>
            </div>

            <div className="farms-grid">
                {filteredFarms.length === 0 ? (
                    <div className="empty-state">
                        <p>No farms found</p>
                    </div>
                ) : (
                    filteredFarms.map(farm => (
                    <div key={farm.id} className="farm-card">
                        <div className="farm-header">
                            <h3>{farm.name}</h3>
                            <span className={`status ${farm.status}`}>
                                {farm.status}
                            </span>
                        </div>

                        <div className="farm-details">
                            <div className="detail-item">
                                <label>Owner</label>
                                <span>{farm.owner}</span>
                            </div>
                            <div className="detail-item">
                                <img src={Map} alt="" />
                                <span>{farm.location}</span>
                            </div>
                            <div className="detail-item">
                                <label>Species</label>
                                <span>{farm.species || 'N/A'}</span>
                            </div>
                            <div className="detail-item">
                                <label>Size</label>
                                <span>{farm.size}</span>
                            </div>
                        </div>

                        <div className="farm-stats">
                            <div className="stat">
                                <label>Total Cases</label>
                                <span>{farm.totalCases}</span>
                            </div>
                            <div className="stat">
                                <label>Active Cases</label>
                                <span>{farm.activeCases}</span>
                            </div>
                        </div>

                        <div className="farm-footer">
                            <span>Registered: {farm.registrationDate}</span>
                            <span>Last active: {farm.lastActivity}</span>
                        </div>

                        <div className="card-actions">
                            <Button
                                text="View Details"
                                variant="outline"
                            />
                            <Button
                                text={farm.status === 'active' ? 'Suspend' : 'Activate'}
                                variant={farm.status === 'active' ? 'error' : 'success'}
                            />
                        </div>
                    </div>
                )))}
            </div>
        </div>
    );
};

export default AdminFarms;